/* ==========================================================================
   DL Spares — global custom dropdown (<dl-select>)
   Progressive enhancement of a native <select>: the real <select> stays in
   the DOM (visually hidden) so form submission, no-JS fallback and a11y
   value handling keep working; this renders a styled button + listbox over
   it. Any `select[data-dl-select]` is auto-wrapped, so the component is the
   global dropdown pattern.
   ========================================================================== */
(function () {
  if (window.customElements && customElements.get('dl-select')) return;

  var CHEVRON =
    '<span class="dl-select__chevron" aria-hidden="true">' +
    '<svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" ' +
    'stroke-width="2.2" stroke-linecap="round" stroke-linejoin="round">' +
    '<polyline points="6 9 12 15 18 9"></polyline></svg></span>';

  class DLSelect extends HTMLElement {
    connectedCallback() {
      this.select = this.querySelector('select');
      if (!this.select || this.dataset.enhanced) return;
      this.dataset.enhanced = 'true';
      this.isOpen = false;
      this._build();
      this._bind();
    }

    _build() {
      this.select.classList.add('dl-select__native');
      this.select.setAttribute('tabindex', '-1');
      this.select.setAttribute('aria-hidden', 'true');

      this.button = document.createElement('button');
      this.button.type = 'button';
      this.button.className = 'dl-select__button';
      this.button.setAttribute('aria-haspopup', 'listbox');
      this.button.setAttribute('aria-expanded', 'false');
      if (this.getAttribute('data-label')) {
        this.button.setAttribute('aria-label', this.getAttribute('data-label'));
      }

      this.valueEl = document.createElement('span');
      this.valueEl.className = 'dl-select__value';
      this.button.appendChild(this.valueEl);
      this.button.insertAdjacentHTML('beforeend', CHEVRON);

      this.panel = document.createElement('div');
      this.panel.className = 'dl-select__panel';
      this.panel.setAttribute('role', 'listbox');

      var self = this;
      this.options = Array.prototype.map.call(this.select.options, function (opt, i) {
        var o = document.createElement('div');
        o.className = 'dl-select__option';
        o.setAttribute('role', 'option');
        o.dataset.index = i;
        o.tabIndex = -1;
        o.textContent = opt.textContent;
        self.panel.appendChild(o);
        return o;
      });

      this.appendChild(this.button);
      this.appendChild(this.panel);
      this._syncLabel();
      this._syncSelected();
    }

    _syncLabel() {
      var opt = this.select.options[this.select.selectedIndex];
      this.valueEl.textContent = opt ? opt.textContent : '';
    }

    _syncSelected() {
      var sel = this.select.selectedIndex;
      this.options.forEach(function (o, i) {
        o.setAttribute('aria-selected', i === sel ? 'true' : 'false');
        o.classList.toggle('is-selected', i === sel);
      });
    }

    _bind() {
      var self = this;
      this.button.addEventListener('click', function () { self.toggle(); });
      this.button.addEventListener('keydown', function (e) { self._onButtonKey(e); });
      this.panel.addEventListener('keydown', function (e) { self._onPanelKey(e); });
      this.options.forEach(function (o) {
        o.addEventListener('click', function () { self.choose(parseInt(o.dataset.index, 10)); });
      });
      document.addEventListener('click', function (e) {
        if (!self.contains(e.target)) self.close();
      });
    }

    toggle() { this.isOpen ? this.close() : this.openPanel(); }

    openPanel() {
      this.isOpen = true;
      this.classList.add('is-open');
      this.button.setAttribute('aria-expanded', 'true');
      var cur = this.options[this.select.selectedIndex] || this.options[0];
      if (cur) cur.focus();
    }

    close() {
      if (!this.isOpen) return;
      this.isOpen = false;
      this.classList.remove('is-open');
      this.button.setAttribute('aria-expanded', 'false');
    }

    choose(index) {
      if (index < 0 || index >= this.select.options.length) return;
      this.select.selectedIndex = index;
      this._syncLabel();
      this._syncSelected();
      this.select.dispatchEvent(new Event('change', { bubbles: true }));
      this.close();
      this.button.focus();
    }

    _onButtonKey(e) {
      if (e.key === 'ArrowDown' || e.key === 'ArrowUp' || e.key === 'Enter' || e.key === ' ') {
        e.preventDefault();
        this.openPanel();
      }
    }

    _onPanelKey(e) {
      var idx = this.options.indexOf(document.activeElement);
      if (e.key === 'ArrowDown') {
        e.preventDefault();
        (this.options[idx + 1] || this.options[0]).focus();
      } else if (e.key === 'ArrowUp') {
        e.preventDefault();
        (this.options[idx - 1] || this.options[this.options.length - 1]).focus();
      } else if (e.key === 'Enter' || e.key === ' ') {
        e.preventDefault();
        this.choose(idx);
      } else if (e.key === 'Escape') {
        e.preventDefault();
        this.close();
        this.button.focus();
      } else if (e.key === 'Tab') {
        this.close();
      }
    }
  }

  customElements.define('dl-select', DLSelect);

  // Auto-wrap any select[data-dl-select] so the modern dropdown is global.
  function autowrap() {
    document.querySelectorAll('select[data-dl-select]').forEach(function (sel) {
      if (sel.closest('dl-select')) return;
      var host = document.createElement('dl-select');
      host.className = 'dl-select';
      if (sel.dataset.label) host.setAttribute('data-label', sel.dataset.label);
      // Move the select INTO the host before connecting the host to the DOM,
      // so connectedCallback runs with the select already present.
      var parent = sel.parentNode;
      var ref = sel.nextSibling;
      host.appendChild(sel);
      parent.insertBefore(host, ref);
    });
  }
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', autowrap);
  } else {
    autowrap();
  }
})();
